import { CloseRounded } from '@mui/icons-material';
import { TabContext, TabPanel } from '@mui/lab';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Dialog,
  Typography,
  IconButton,
  Button,
  ListItem,
  Stepper,
  Step,
  StepLabel,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Slide,
} from '@mui/material';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { variants } from '../../../../variants/variants';
import { rocket, crash, takeoff, empty } from '../../assets/index';
import '@smastrom/react-rating/style.css';
import { useSubmitFeedBackMutation } from '../../giftApiSlice';
import FeedbackRating from './FeedbackRating';
import CandidateOverview from '../Candidate/CandidateOverview';
import { useMediaQuery } from '@mui/material';

const Transition = forwardRef(function Transition (props, ref) {
  return <Slide direction='up' ref={ref} {...props} />;
});
const initialFormState = {
  positivePoints: [],
  areasOfImprovement: [],
  plan: '',
  conclusion: 'qualifies',
  rating: 3,
};
const steps = ['Positive points', 'Areas of improvement', 'Plan', 'Conclusion'];

const FeedbackForm = ({ open, handleClose, selected, feedBackAvailable = false }) => {
  const { id, formDetails } = selected;
  const [activeStep, setActiveStep] = useState(0);
  const [submitFeedback] = useSubmitFeedBackMutation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const [formBody, setFormBody] = useState({
    positivePoints: [],
    areasOfImprovement: [],
    plan: '',
    conclusion: 'qualifies',
    rating: 3,
  });
  const [input, setInput] = useState('');
  const handleSubmit = () => {
    setFormBody(initialFormState);
    handleClose();
    setActiveStep(0);
    setInput('');
    submitFeedback({ interviewId: id, form: { ...formBody, conclusion: formBody.conclusion === 'qualifies' } });
  };
  const handleRating = (value) => {
    setFormBody({ ...formBody, rating: value });
  };
  const handleAddClick = (type) => {
    if (input !== '') {
      let newArray = [...formBody[type], input];
      let newObject = { ...formBody };
      newObject[type] = newArray;
      setFormBody(newObject);
      setInput('');
    }
  };
  const handleInputChange = (e, key) => {
    const value = e.target.value;
    setInput(value);
    if (key) {
      setFormBody({ ...formBody, [key]: value });
    }
  };
  const handleKeyEnter = (e, type) => {
    if (e.code === 'Enter') {
      handleAddClick(type);
    }
  };
  const handleConclusion = (e, value) => {
    setFormBody({ ...formBody, conclusion: value });
  };
  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setInput('');
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setInput('');
  };
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.lastElementChild?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [formBody, activeStep]);
  return (
    <Dialog
      fullScreen={isMobile ? true : false}
      open={open}
      handleClose={handleClose}
      maxWidth='md'
      fullWidth
      TransitionComponent={Transition}
    >
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, p: 1 }}>
        <Stack direction='row' alignItems={'center'}>
          <Typography>GIFT Technical Jury feedback form</Typography>
          <IconButton sx={{ ml: 'auto' }} {...(open && { className: 'backButton' })} onClick={handleClose}>
            <CloseRounded fontSize='small' />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 1.5 }}>
        <Stack>
          <CandidateOverview formDetails={formDetails} />
          <TabContext value={String(activeStep)}>
            {/* Positive points */}
            <TabPanel
              value='0'
              sx={{ height: '40vh', p: 1, border: 1, borderColor: 'divider', borderRadius: variants.boxRadius.MAIN }}
            >
              <Typography sx={{ fontSize: variants.BODY, color: variants.typography.inactive.hover }}>
                Please write the positive points about this candidate:{' '}
              </Typography>
              <Stack direction={'row'} sx={{ mt: 1 }}>
                <TextField
                  value={input}
                  onChange={handleInputChange}
                  placeholder='Enter a positive point about candidate...'
                  fullWidth
                  size='small'
                  onKeyDown={(e) => handleKeyEnter(e, 'positivePoints')}
                />
                <Button
                  size='small'
                  variant='contained'
                  sx={{ ml: 1 }}
                  onClick={() => handleAddClick('positivePoints')}
                >
                  Add
                </Button>
              </Stack>
              <div className='content' style={{ padding: 0 }}>
                <Stack sx={{ height: '30vh', overflowY: 'auto', mt: 1 }} ref={messagesEndRef}>
                  {formBody.positivePoints.length === 0 && <img src={empty} style={{ width: 150, margin: 'auto' }} />}
                  {formBody.positivePoints.map((item, index) => {
                    return (
                      <ListItem key={index}>
                        <img src={rocket} style={{ width: 20, height: 20 }} />. {item}
                      </ListItem>
                    );
                  })}
                </Stack>
              </div>
            </TabPanel>

            {/* Area of improvement */}
            <TabPanel
              value='1'
              sx={{ height: '40vh', p: 1, border: 1, borderColor: 'divider', borderRadius: variants.boxRadius.MAIN }}
            >
              <Typography sx={{ fontSize: variants.BODY, color: variants.typography.inactive.hover }}>
                Please write the Areas of improvement for this candidate:
              </Typography>
              <Stack direction={'row'} sx={{ mt: 1 }}>
                <TextField
                  value={input}
                  onChange={handleInputChange}
                  placeholder='Enter scope of improvement for candidate...'
                  fullWidth
                  size='small'
                  onKeyDown={(e) => handleKeyEnter(e, 'areasOfImprovement')}
                />
                <Button
                  size='small'
                  variant='contained'
                  sx={{ ml: 1 }}
                  onClick={() => handleAddClick('areasOfImprovement')}
                >
                  Add
                </Button>
              </Stack>
              <div className='content' style={{ padding: 0 }}>
                <Stack sx={{ height: '30vh', overflowY: 'auto', mt: 1 }} ref={messagesEndRef}>
                  {formBody.areasOfImprovement.length === 0 && (
                    <img src={empty} style={{ width: 150, margin: 'auto' }} />
                  )}
                  {formBody.areasOfImprovement.map((item, index) => {
                    return (
                      <ListItem key={index}>
                        <img src={rocket} style={{ width: 20, height: 20 }} />. {item}
                      </ListItem>
                    );
                  })}
                </Stack>
              </div>
            </TabPanel>

            {/* Plan */}
            <TabPanel
              value='2'
              sx={{ height: '40vh', p: 1, border: 1, borderColor: 'divider', borderRadius: variants.boxRadius.MAIN }}
            >
              <Typography sx={{ fontSize: variants.BODY, color: variants.typography.inactive.hover }}>
                Please write the future plan for this candidate:
              </Typography>

              <TextField
                multiline
                sx={{ mt: 2 }}
                rows={12}
                maxRows={12}
                value={formBody.plan}
                onChange={(e) => handleInputChange(e, 'plan')}
                placeholder='Enter scope of improvement for candidate...'
                fullWidth
                size='small'
              />
            </TabPanel>

            {/* Conclusion */}
            <TabPanel
              value='3'
              sx={{ height: '40vh', p: 1, border: 1, borderColor: 'divider', borderRadius: variants.boxRadius.MAIN }}
            >
              <Typography sx={{ fontSize: variants.BODY, color: variants.typography.inactive.hover }}>
                Conclusion
              </Typography>
              <Box sx={{ mx: 'auto', width: isMobile ? '100%' : '50%', display: 'grid', placeItems: 'center' }}>
                <ToggleButtonGroup value={formBody.conclusion} exclusive onChange={handleConclusion}>
                  <ToggleButton value='qualifies'>
                    <Typography sx={{ fontSize: variants.TINY, mr: 1 }}>Qualifies</Typography>
                    <img src={takeoff} style={{ width: 50, height: 50 }} />
                  </ToggleButton>
                  <ToggleButton value='does not qualify'>
                    <Typography sx={{ fontSize: variants.TINY, mr: 1 }}>Does not qualify</Typography>

                    <img src={crash} style={{ width: 50, height: 50 }} />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Typography sx={{ fontSize: variants.BODY, fontWeight: 600, mx: 'auto', mt: 2, width: 'max-content' }}>
                The candidate {formBody.conclusion} for the aspired role.
              </Typography>
              <FeedbackRating rating={formBody.rating} handleRating={handleRating} />
            </TabPanel>
          </TabContext>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 1 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Stack>
      </DialogContent>
      {!feedBackAvailable && (
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Stack direction={'row'} sx={{ display: 'flex', flexDirection: 'row', width: 1 }}>
            <Button color='inherit' disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
            <Button variant='contained' onClick={handleNext} sx={{ ml: 'auto' }}>
              {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
            </Button>
          </Stack>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default FeedbackForm;
